import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useContent } from "../../context/ContentContext";
import { Link } from "react-router-dom";

const T = {
  pt: { name: "O teu nome", email: "O teu email", subject: "Assunto (opcional)", message: "Do que precisas? Diz-nos.", send: "Enviar mensagem", sending: "A enviar...", required: "Preenche o nome, email e mensagem.", fail: "Falha ao enviar.", privacy: "Ao enviar, aceitas o tratamento dos teus dados conforme a", policy: "Política de Privacidade" },
  en: { name: "Your name", email: "Your email", subject: "Subject (optional)", message: "What do you need? Tell us.", send: "Send message", sending: "Sending...", required: "Please fill in name, email and message.", fail: "Failed to send.", privacy: "By submitting, you accept the processing of your data per our", policy: "Privacy Policy" },
};

export default function Contact({ content }) {
  const { locale } = useContent();
  const t = T[locale] || T.pt;
  const c = content?.contact || {};
  const legal = content?.legal || {};
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.required);
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(c.successMessage || "Mensagem enviada!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.fail);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b-2 border-peca-line focus:border-peca-yellow outline-none py-4 text-peca-cream placeholder:text-peca-grey/60 font-body transition-colors duration-200";

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 md:py-40 bg-peca-surface border-t border-peca-line">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5"
          >
            <span className="label-mono text-peca-yellow">✳ {c.eyebrow}</span>
            <h2 className="mt-5 font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] text-peca-cream">
              {c.title}
            </h2>
            <p className="mt-6 text-peca-grey text-base md:text-lg leading-relaxed max-w-md">{c.subtitle}</p>
            <a
              href={`mailto:${c.email}`}
              data-testid="contact-email-link"
              className="mt-8 inline-flex items-center gap-2 font-display font-bold text-xl md:text-2xl text-peca-cream hover:text-peca-yellow transition-colors duration-200 break-all"
            >
              {c.email}
              <ArrowUpRight size={22} />
            </a>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-6 md:col-start-7 flex flex-col gap-6"
          >
            <input data-testid="contact-name" className={inputCls} placeholder={t.name} value={form.name} onChange={update("name")} />
            <input data-testid="contact-email" type="email" className={inputCls} placeholder={t.email} value={form.email} onChange={update("email")} />
            <input data-testid="contact-subject" className={inputCls} placeholder={t.subject} value={form.subject} onChange={update("subject")} />
            <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder={t.message} value={form.message} onChange={update("message")} />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="group mt-2 inline-flex items-center justify-center gap-2 bg-peca-yellow text-peca-black font-display font-bold uppercase text-sm tracking-wide px-8 py-5 hover:bg-peca-cream transition-colors duration-200 disabled:opacity-60"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : null}
              {sending ? t.sending : t.send}
              {!sending && <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-200" />}
            </button>
            <p className="text-peca-grey/70 text-xs leading-relaxed" data-testid="contact-privacy-note">
              {legal.contactPrivacy || t.privacy}{" "}
              <Link to="/privacidade" className="text-peca-yellow underline underline-offset-2 hover:text-peca-cream transition-colors">{legal.contactPrivacyLabel || t.policy}</Link>.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
